'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import useDebounce from '@/hooks/useDebounce';
import useRecentSearches from '@/hooks/useRecentSearches';
import useSearchResults from '@/hooks/useSearchResults';
import { SEARCH_DEBOUNCE_MS, SEARCH_PATH } from '@/constants/search';
import formatSearchKeyword from '@/utils/search/formatSearchKeyword';
import type { SearchResultItem } from '@/types/search';

/**
 * State for the header search field — `genai-studio-v2`'s `useSearch`, minus
 * the parts that only make sense inside the miniapp.
 *
 * Results follow typing: `value` is what's typed, `query` is the normalized
 * term that reaches the request, and the debounce between them collapses a
 * burst of keystrokes into one call. A clear or a recent-search tap commits
 * immediately and skips the wait.
 *
 * Two deliberate differences from the miniapp, both because this is a site and
 * that is an app: opening a result navigates to the preset's page rather than
 * writing to a store, and submitting goes to `/search`, which the miniapp has
 * no equivalent of. The request underneath is the same one.
 */
const useSearch = () => {
  const router = useRouter();
  const pathname = usePathname();
  const [value, setValue] = useState('');
  const [query, setQuery] = useState('');

  const { recentSearches, remember, forget, forgetAll } = useRecentSearches();
  const { items, restrictionMessage, isFetching, isError, hasNoResults, retry } = useSearchResults(query);

  const debouncedValue = useDebounce(value, SEARCH_DEBOUNCE_MS);

  // What the debounce settles on becomes the term, unless something already
  // committed one ahead of it (clear, recent tap) — `setQuery` to the same
  // string is a no-op, so that case costs nothing.
  useEffect(() => {
    setQuery(formatSearchKeyword(debouncedValue));
  }, [debouncedValue]);

  const commit = useCallback((term: string) => {
    setValue(term);
    setQuery(term);
  }, []);

  /**
   * On the results page, the field shows what the page is showing.
   *
   * Without this the two disagreed outright: `/search?q=reddad` listed its
   * results while the field beside it sat empty, and focusing it offered recent
   * searches instead of the search you were looking at.
   *
   * Read from `window.location` rather than `useSearchParams()`: this hook runs
   * in the root layout's header, on every route, and `useSearchParams()` there
   * opts every statically rendered page into client-side rendering unless each
   * is wrapped in its own Suspense boundary. `usePathname()` carries no such
   * cost and still fires on every navigation, which is when the query can have
   * changed.
   */
  useEffect(() => {
    if (pathname !== SEARCH_PATH) return;
    commit(formatSearchKeyword(new URLSearchParams(window.location.search).get('q') ?? ''));
  }, [pathname, commit]);

  const handleChange = useCallback((nextValue: string) => setValue(nextValue), []);

  // Recording every keystroke would fill the list with prefixes, so a term is
  // only remembered once the user commits to it — submitting, or opening one of
  // its results.
  const handleSubmit = useCallback(() => {
    const term = formatSearchKeyword(value);
    if (!term) return;

    remember(term);
    router.push(`/search?q=${encodeURIComponent(term)}`);
  }, [value, remember, router]);

  const handleClear = useCallback(() => commit(''), [commit]);

  const handleRecentSelect = useCallback(
    (term: string) => {
      // Committed rather than debounced — a tap on a past search shouldn't sit
      // through the wait.
      commit(term);
      remember(term);
    },
    [commit, remember],
  );

  const handleResultClick = useCallback(
    (item: SearchResultItem) => {
      // Results with no originating preset have nowhere to go — they render
      // non-interactive, so this is only a belt-and-braces guard.
      if (!item.presetSlug || !item.categorySlug) return;

      remember(query);
      router.push(`/${item.categorySlug}/${item.presetSlug}`);
    },
    [remember, query, router],
  );

  return {
    value,
    query,
    items,
    recentSearches,
    restrictionMessage,
    isFetching,
    isError,
    hasNoResults,
    handleChange,
    handleSubmit,
    handleClear,
    handleResultClick,
    handleRecentSelect,
    handleRecentRemove: forget,
    handleRecentClearAll: forgetAll,
    handleRetry: retry,
  };
};

export default useSearch;
